// src/services/applicationParser.ts

import { ApplicationConfigFile } from "@/types/front/ApplicationConfigFile";
import { ErrorsMessage } from "@/types/front/ErrorsMessage";
import { ApplicationResult, LoginAdminResult } from "@/types/generated/UsedGeneratedTypes";

export interface Chunk {
  result: ApplicationResult;
}

export interface Props {
  applicationVersion: string;
  applicationName: string;
  applicationNames?: string[];
}

export interface Services {
  alertService: {
    toastSuccess: (msg: string | unknown) => void;
    toastError: (msg: string | unknown) => void;
  };
  errorsService: {
    getErrorsMessages: (
      errors: string,
      params: Record<string, unknown>,
      type: string,
    ) => string;
  };
}

export interface Router {
  push: (path: string) => void;
}

export interface Ref<T> {
  value: T;
}

export interface Context {
  applicationConfig: ApplicationConfigFile;
  props: Props;
  currentUser: LoginAdminResult;
  services: Services;
  router: Router;
  changeBtnUpdateConfig: (v: boolean) => void;
  disabled: Ref<boolean>;
  validate: Ref<boolean>;
  errorsMessages: Ref<Array<ErrorsMessage | string>>;
  t: (key: string, params?: Record<string, unknown>) => string;
}

/**
 * Vérifie si une chaîne correspond à au moins une regex dans un tableau
 */
export function testStringWithRegexArray(
  str: string,
  regexArray: string[],
): boolean {
  return regexArray.some((regexStr) => {
    const regex = new RegExp(regexStr);
    return regex.test(str);
  });
}

/**
 * Fonction principale de parsing
 */
export function parseResultChunck(
  chunk: Chunk,
  caller: "validate" | "create" | "change",
  ctx: Context,
): void {
  const {
    applicationConfig,
    props,
    currentUser,
    services,
    router,
    changeBtnUpdateConfig,
    disabled,
    validate,
    errorsMessages,
    t,
  } = ctx;

  const response = chunk.result;

  if ("validate" === caller && response.name) {
    if (
      response.configuration.applicationDescription.name.length < 44
      && (currentUser.authorizations.includes(
        response.configuration.applicationDescription.name.toLowerCase(),
      )
      || testStringWithRegexArray(
        response.configuration.applicationDescription.name.toLowerCase(),
        currentUser.authorizations,
      ))
    ) {
      applicationConfig.name
        = response.configuration.applicationDescription.name.toLowerCase();
      applicationConfig.version
        = response.configuration.applicationDescription.version.version;
      applicationConfig.comment
        = response.configuration.applicationDescription.comment;

      if (
        applicationConfig.version.toString() !== props.applicationVersion
        && applicationConfig.name === props.applicationName
      ) {
        changeBtnUpdateConfig(true);
      }

      if (
        applicationConfig.version.toString() === props.applicationVersion
        && applicationConfig.name === props.applicationName
      ) {
        disabled.value = true;
        services.alertService.toastError(
          t("alert.server-error-appli-exist", {
            name: props.applicationName,
            version: props.applicationVersion,
          }),
        );
        validate.value = false;
      }
      else if (props.applicationNames?.includes(applicationConfig.name!)) {
        disabled.value = true;
        services.alertService.toastError(
          t("alert.server-error-appli-exist-on-creation", {
            applicationName: applicationConfig.name,
          }),
        );
        validate.value = false;
      }
      else if (
        props.applicationName !== ""
        && props.applicationName !== applicationConfig.name
      ) {
        disabled.value = true;
        services.alertService.toastError(
          t("alert.name-application-not-accept", {
            applicationName: props.applicationName,
          }),
        );
        validate.value = false;
      }
      else {
        services.alertService.toastSuccess(
          t("alert.application-validate-success"),
        );
        validate.value = true;
      }
    }
    else {
      disabled.value = true;
      const message
        = response.configuration.applicationDescription.name.toString().length
          < 48
          ? "badNameWithRight"
          : "invalidLengthName";
      const params
        = message === "invalidLengthName"
          ? {}
          : { name: response.configuration.applicationDescription.name };
      const errorsMessage = new ErrorsMessage();
      errorsMessage.type = message;
      errorsMessage.message = services.errorsService.getErrorsMessages(
        message,
        params,
        "errors-yaml.",
      );
      errorsMessage.title = t("errors-yaml." + message + ".title");
      errorsMessage.params = params;
      errorsMessages.value.push(errorsMessage);
    }
  }
  else if ("create" === caller) {
    services.alertService.toastSuccess(
      t("alert.application-creation-success"),
    );
    router.push("/applications");
  }
  else if ("change" === caller) {
    services.alertService.toastSuccess(t("alert.application-edit-success"));
    router.push("/applications");
  }
}
